import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityData,
  ActivityUpdateData,
  ActivityFilters,
  EntityActivityRequest,
  EntityActivityResponse,
  Filters,
  TranscriptProcessingResponse
} from "@/entities/activities/types";
import { getProcessingKey } from "@/entities/activities/helpers";
import { useEntities } from "@/services/api/entityhooks";
import { EntitySliceState } from "@/services/api/entitystore";
import useAPI, { useAPIMutation } from "@/services/api/hook";
import { createUseFilteredEntitiesInfinite } from "@/services/api/infinite";
import { APIObject, EmptyFilters, SearchQuery } from "@/services/api/types";
import useBoundStore, {
  MAX_ENTITIES_PER_PAGE
} from "@/services/store/BoundStore";

export const MAX_ACTIVITIES_PER_PAGE = MAX_ENTITIES_PER_PAGE;

export const applyActivityUpdateResponse = (
  updateActivity: (id: string, updates: Partial<ActivityData>) => void,
  response: ActivityData
): void => {
  updateActivity(response.id, response);
};

// TODO: remove and fix the usage in ActivityModal
export const useActivities = (ids: string[]) => {
  const useActivitiesStore = (): EntitySliceState<ActivityData> => {
    return useBoundStore((state) => state.activities);
  };

  return useEntities<ActivityData>(
    ids,
    "activities",
    useActivitiesStore,
    useAPI
  );
};

export const { useFilteredEntitiesInfinite } =
  createUseFilteredEntitiesInfinite<ActivityData, ActivityFilters>(
    "activities",
    MAX_ACTIVITIES_PER_PAGE
  );

export const useFilteredActivities = (
  pageName: string,
  entityId: string,
  filters: Filters = EmptyFilters,
  search?: SearchQuery,
  groupedBy?: keyof ActivityData,
  pageSize = MAX_ACTIVITIES_PER_PAGE,
  sortField?: string,
  sortAsc?: boolean,
  fetchAll = true,
  enabled = true
) => {
  return useFilteredEntitiesInfinite(
    () => ({
      addEntity: useBoundStore((state) => state.activities.addEntity)
    }),
    {
      filters,
      search,
      groupedBy,
      pageSize,
      sortField,
      sortAsc,
      fetchAll,
      // Activities must have some kind of filtering as non-filtered requests will fail
      enabled: enabled && filters !== EmptyFilters && entityId !== ""
    },
    pageName,
    entityId
  );
};

export const useCreateActivityMutation = (pageName: string) => {
  const addActivity = useBoundStore((state) => state.activities.addEntity);
  return useAPIMutation<
    {
      pathParams: {
        entityId: string;
      };
      data: ActivityData;
    },
    APIObject
  >(
    `${pageName}/createActivity`,
    {
      method: "POST"
    },
    (response) => {
      addActivity({
        ...response,
        id: response.id
      } as ActivityData);
    }
  );
};

export const useUpdateActivityMutation = (pageName: string) => {
  const updateActivity = useBoundStore(
    (state) => state.activities.updateEntity
  );
  return useAPIMutation<
    {
      pathParams: { entityId: string; activityId: string };
      data: ActivityUpdateData;
    },
    ActivityData
  >(
    `${pageName}/activities/id`,
    {
      method: "PUT"
    },
    (response) => {
      applyActivityUpdateResponse(updateActivity, response);
    }
  );
};

export const useDeleteActivityMutation = (pageName: string) => {
  const removeActivity = useBoundStore(
    (state) => state.activities.removeEntity
  );
  return useAPIMutation<
    { pathParams: { entityId: string; activityId: string } },
    void
  >(
    `${pageName}/activities/id`,
    {
      method: "DELETE"
    },
    (_response, data) => {
      removeActivity(data.pathParams.activityId);
    }
  );
};

export const useEntityActivitiesFetch = (pageName: string) => {
  return useAPIMutation<
    {
      pathParams: { entityId: string };
      data: EntityActivityRequest;
    },
    EntityActivityResponse
  >(`${pageName}/activities/fetch`, {
    method: "POST"
  });
};

// While a transcript is analyzed the summary + autofill fields are regenerated
// server-side (~1-2 min), so poll for the meeting's processing state to clear.
const TRANSCRIPT_PROCESSING_POLL_MS = 15000;

/**
 * Record-level transcript-processing signal for the Detailed view banner.
 * Hits a lightweight endpoint that returns only the ids of meetings still being
 * analyzed, instead of pulling the record's full meeting history. Polls while
 * any are processing so the banner disappears on its own once they clear.
 */
export const useRecordTranscriptProcessing = (
  pageName: string,
  entityId: string,
  enabled: boolean
): { isProcessing: boolean; processingKey: string | null } => {
  const options = useMemo(
    () => ({
      method: "GET" as const,
      pathParams: { entityId }
    }),
    [entityId]
  );

  const { data } = useAPI(
    `${pageName}/activities/transcriptProcessing`,
    options,
    {
      enabled: enabled && !!entityId,
      refetchInterval: (query) =>
        ((query.state.data as TranscriptProcessingResponse | undefined)
          ?.processingActivityIds?.length ?? 0) > 0
          ? TRANSCRIPT_PROCESSING_POLL_MS
          : false
    }
  );

  const processingIds =
    (data as TranscriptProcessingResponse | undefined)?.processingActivityIds ??
    [];

  return {
    isProcessing: processingIds.length > 0,
    processingKey: getProcessingKey(processingIds)
  };
};

const TRANSCRIPT_BANNER_DISMISSED_KEY =
  "transcript-processing-banner-dismissed";

// entityId -> the processing-window key that was dismissed for that record.
type DismissedTranscriptWindows = Record<string, string>;

const readDismissedTranscriptWindows = (): DismissedTranscriptWindows => {
  try {
    const saved = localStorage.getItem(TRANSCRIPT_BANNER_DISMISSED_KEY);
    const parsed = saved ? JSON.parse(saved) : null;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as DismissedTranscriptWindows)
      : {};
  } catch {
    return {};
  }
};

/**
 * Persists the Detailed view transcript banner dismissal per processing window.
 * Keyed by the record and the set of meetings being analyzed (`processingKey`),
 * so a dismissal survives a refresh while those transcripts are still
 * processing, yet the banner re-appears once a different meeting starts
 * processing.
 */
export const useTranscriptBannerDismissal = (
  entityId: string,
  processingKey: string | null
): { isDismissed: boolean; dismiss: () => void } => {
  const [dismissedWindows, setDismissedWindows] =
    useState<DismissedTranscriptWindows>(readDismissedTranscriptWindows);

  useEffect(() => {
    try {
      localStorage.setItem(
        TRANSCRIPT_BANNER_DISMISSED_KEY,
        JSON.stringify(dismissedWindows)
      );
    } catch {
      // Dismissal is a convenience; ignore storage failures (quota/private mode).
    }
  }, [dismissedWindows]);

  const isDismissed =
    !!entityId &&
    processingKey !== null &&
    dismissedWindows[entityId] === processingKey;

  const dismiss = useCallback(() => {
    if (!entityId || processingKey === null) return;
    setDismissedWindows((previous) => ({
      ...previous,
      [entityId]: processingKey
    }));
  }, [entityId, processingKey]);

  return { isDismissed, dismiss };
};
